/**
 * Marketing OS — Arthaus offer renderer ("gallery placard")
 * Contract: window.mos v0.2 — docs/SURFACES-SDK.md (Avant-Garde-AI/marketing-os)
 *
 * Theme-native renderer for surface type "offer", replacing the runtime's
 * built-in corner card. Design language: a gallery placard — the small wall
 * label beside an artwork. Mono-caps eyebrow, Playfair display headline,
 * hairline border on white. Bottom-LEFT corner panel on desktop (the right
 * side belongs to cart drawer + toasts), full-width bottom sheet on mobile.
 *
 * Division of labor (SDK contract §1/§5 — gate-enforced):
 *   - The SDK decides whether/when to show, which arm, and owns all measurement.
 *   - This file owns pixels only. Closes go through ctl.dismiss(), submissions
 *     through ctl.capture(), the impression through ctl.mount(el). Copy in
 *     ctl.variant.content is merchant-approved and rendered VERBATIM (via
 *     textContent — never innerHTML interpolation).
 *   - Fails invisible: if window.mos never arrives, nothing runs, nothing logs.
 *
 * Styles are injected from here (not a <link> in the layout): the earliest
 * trigger fires ~10s after load, so a render-blocking stylesheet in <head>
 * would be pure cost for a surface most pageviews never show. Injection also
 * keeps markup + style atomic. (Theme precedent: discovery-grid.js. Note the
 * theme's Tailwind is AOT-compiled, so JS-built markup can't use arbitrary
 * utility classes — all styling lives on .mos-ofr-* classes.)
 *
 * Brand tokens from ctl.variant.style/ctl.tokens are honored via --mos-*
 * custom properties, falling back to Arthaus tokens (tokens.css), falling
 * back to literals so the surface renders correctly on any theme branch.
 * Two deliberate theme-system choices (SDK §4 allows either): the headline
 * uses our display serif, and the CTA uses our charcoal button (guaranteed
 * AA contrast; the accent token stays on eyebrow + focus rings).
 */
(function () {
  'use strict';

  var STYLE_ID = 'mos-ofr-style';
  var EASE = 'cubic-bezier(.22,.68,.28,1)'; // matches the theme's gridwall easing

  /* ── Registration (pre-init-queue safe, SDK §3) ─────────────────────────── */
  var registered = false;
  function register() {
    if (registered) return; // queue drain AND mos:ready may both fire
    registered = true;
    try {
      window.mos.surfaces.register('offer', renderOffer);
    } catch (e) { /* fails invisible */ }
  }
  if (window.mos && window.mos.surfaces && window.mos.surfaces.register) {
    register();
  } else {
    (window.__mosQueue = window.__mosQueue || []).push(register);
    document.addEventListener('mos:ready', register, { once: true });
  }

  /* ── Styles ─────────────────────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.mos-ofr{' +
        'position:fixed;left:1.25rem;bottom:1.25rem;' +
        /* under cart drawer (60) / modal (70) / toast (80) — a marketing
           surface never covers commerce UI; above content + sticky bar (40) */
        'z-index:55;' +
        'box-sizing:border-box;width:min(23.5rem,calc(100vw - 2.5rem));' +
        'background:var(--mos-bg,rgb(var(--color-white,255 255 255)));' +
        'color:var(--mos-ink,rgb(var(--color-charcoal,45 45 45)));' +
        'font-family:var(--mos-font,var(--font-body,"DM Sans","Helvetica Neue",Helvetica,Arial,sans-serif));' +
        'border:1px solid var(--mos-line,rgb(var(--color-border,232 228 223)));' +
        'border-radius:.75rem;' +
        'box-shadow:0 14px 40px -18px rgba(45,45,45,.28),0 2px 8px rgba(45,45,45,.06);' +
        'padding:1.5rem 1.5rem 1.375rem;' +
        'opacity:0;transform:translateY(.875rem);' +
        'transition:opacity .45s ' + EASE + ',transform .5s ' + EASE + ';' +
      '}' +
      '.mos-ofr.mos-ofr--in{opacity:1;transform:none;}' +
      '.mos-ofr *{box-sizing:border-box;margin:0;}' +
      '.mos-ofr__x{' +
        'position:absolute;top:.625rem;right:.625rem;width:2rem;height:2rem;' +
        'display:flex;align-items:center;justify-content:center;' +
        'background:none;border:0;border-radius:9999px;cursor:pointer;padding:0;' +
        'color:var(--mos-ink2,rgb(var(--color-warm-gray,107 101 96)));' +
        'font-size:1.125rem;line-height:1;' +
      '}' +
      '.mos-ofr__x:hover{color:var(--mos-ink,rgb(var(--color-charcoal,45 45 45)));}' +
      '.mos-ofr__eyebrow{' +
        'font-family:var(--font-mono,"IBM Plex Mono",Menlo,monospace);' +
        'font-size:.6875rem;letter-spacing:.14em;text-transform:uppercase;' +
        'color:var(--mos-accent,rgb(var(--color-bronze,176 125 79)));' +
        'margin-bottom:.625rem;padding-right:2rem;' +
      '}' +
      '.mos-ofr__headline{' +
        'font-family:var(--font-display,"Playfair Display",Georgia,serif);' +
        'font-weight:400;font-size:1.375rem;line-height:1.25;letter-spacing:-.01em;' +
        'margin-bottom:.5rem;padding-right:1.25rem;' +
      '}' +
      '.mos-ofr__body{' +
        'font-size:.875rem;line-height:1.55;font-weight:300;' +
        'color:var(--mos-ink2,rgb(var(--color-warm-gray,107 101 96)));' +
        'margin-bottom:1rem;' +
      '}' +
      '.mos-ofr__form{display:flex;gap:.5rem;flex-wrap:wrap;}' +
      '.mos-ofr__input{' +
        'flex:1 1 10rem;min-width:0;height:2.75rem;padding:0 .875rem;' +
        'font:inherit;font-size:.875rem;' +
        'color:var(--mos-ink,rgb(var(--color-charcoal,45 45 45)));' +
        'background:var(--mos-bg,rgb(var(--color-white,255 255 255)));' +
        'border:1px solid var(--mos-line,rgb(var(--color-border,232 228 223)));' +
        'border-radius:.5rem;appearance:none;' +
      '}' +
      '.mos-ofr__input::placeholder{color:var(--mos-ink2,rgb(var(--color-warm-gray,107 101 96)));opacity:1;}' +
      '.mos-ofr__cta{' +
        'height:2.75rem;padding:0 1.125rem;font:inherit;font-size:.875rem;font-weight:500;' +
        'color:rgb(var(--color-white,255 255 255));' +
        'background:rgb(var(--color-charcoal,45 45 45));' +
        'border:0;border-radius:.5rem;cursor:pointer;white-space:nowrap;' +
        'transition:opacity .2s ease;' +
      '}' +
      '.mos-ofr__cta:hover{opacity:.85;}' +
      '.mos-ofr__cta[disabled]{opacity:.55;cursor:default;}' +
      '.mos-ofr__status{font-size:.8125rem;line-height:1.4;margin-top:.5rem;}' +
      '.mos-ofr__status:empty{display:none;}' +
      '.mos-ofr__success{' +
        'font-size:.9375rem;line-height:1.5;padding:.375rem 0 .125rem;' +
        'color:var(--mos-ink,rgb(var(--color-charcoal,45 45 45)));' +
      '}' +
      '.mos-ofr__consent{' +
        'display:block;font-size:.6875rem;line-height:1.45;font-weight:300;' +
        'color:var(--mos-ink2,rgb(var(--color-warm-gray,107 101 96)));' +
        'margin-top:.75rem;' +
      '}' +
      '.mos-ofr :focus-visible{' +
        'outline:2px solid var(--mos-accent,rgb(var(--color-bronze,176 125 79)));' +
        'outline-offset:2px;border-radius:.25rem;' +
      '}' +
      /* Mobile: bottom sheet — corner cards cramp at 390px */
      '@media (max-width:39.9375rem){' +
        '.mos-ofr{' +
          'left:0;right:0;bottom:0;width:auto;' +
          'border-radius:1rem 1rem 0 0;border-left:0;border-right:0;border-bottom:0;' +
          'padding:1.375rem 1.25rem calc(1.125rem + env(safe-area-inset-bottom,0px));' +
        '}' +
      '}' +
      '@media (prefers-reduced-motion:reduce){' +
        '.mos-ofr{transition:none;opacity:1;transform:none;}' +
      '}';
    document.head.appendChild(s);
  }

  /* ── Small DOM helper: verbatim, XSS-safe text nodes ────────────────────── */
  function make(tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text; // verbatim — never innerHTML
    return el;
  }

  /* ── Renderer ───────────────────────────────────────────────────────────── */
  function renderOffer(ctl) {
    var c = ctl.variant.content;
    var tokens = (ctl.variant && ctl.variant.style) || ctl.tokens || {};

    injectStyles();

    var el = make('aside', 'mos-ofr');
    el.setAttribute('role', 'complementary');
    el.setAttribute('aria-label', c.headline);
    el.setAttribute('data-mos-surface', (ctl.surface && ctl.surface.id) || '');

    // Honor merchant brand tokens (SDK §4); Arthaus tokens remain the fallback.
    ['bg', 'ink', 'ink2', 'accent', 'line'].forEach(function (k) {
      if (tokens[k]) el.style.setProperty('--mos-' + k, tokens[k]);
    });
    if (tokens.font && tokens.font !== 'inherit') el.style.setProperty('--mos-font', tokens.font);

    // ✕ — one tap, plainly labeled, remembered (suppression is SDK-managed).
    var x = make('button', 'mos-ofr__x', '×');
    x.type = 'button';
    x.setAttribute('aria-label', 'Dismiss');

    if (c.eyebrow) el.appendChild(make('p', 'mos-ofr__eyebrow', c.eyebrow));
    el.appendChild(x);
    el.appendChild(make('p', 'mos-ofr__headline', c.headline));
    el.appendChild(make('p', 'mos-ofr__body', c.body));

    var form = make('form', 'mos-ofr__form');
    form.noValidate = false;
    var input = make('input', 'mos-ofr__input');
    input.type = 'email';
    input.required = true;
    input.autocomplete = 'email';
    input.placeholder = c.placeholder || '';
    input.setAttribute('aria-label', 'Email address');
    var cta = make('button', 'mos-ofr__cta', c.cta);
    cta.type = 'submit';
    form.appendChild(input);
    form.appendChild(cta);
    el.appendChild(form);

    // Quiet inline status line (retry state), announced politely.
    var status = make('p', 'mos-ofr__status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    el.appendChild(status);

    var consent = make('small', 'mos-ofr__consent', c.consent);
    el.appendChild(consent);

    /* Close — the ONLY path is ctl.dismiss(); restore focus if it was inside. */
    var lastOutsideFocus = null;
    document.addEventListener('focusin', rememberOutsideFocus, true);
    function rememberOutsideFocus(e) {
      if (!el.contains(e.target)) lastOutsideFocus = e.target;
    }
    function close() {
      var hadFocus = el.contains(document.activeElement);
      document.removeEventListener('focusin', rememberOutsideFocus, true);
      ctl.dismiss(); // suppression + event + removal — never just .remove()
      if (hadFocus && lastOutsideFocus && document.contains(lastOutsideFocus)) {
        try { lastOutsideFocus.focus(); } catch (e) { /* noop */ }
      }
    }
    x.addEventListener('click', close);
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.stopPropagation(); close(); }
    });

    /* Submit — the ONLY path is ctl.capture(). */
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (cta.disabled) return;
      status.textContent = '';
      cta.disabled = true;
      el.setAttribute('aria-busy', 'true');
      ctl.capture(input.value)
        .then(function (res) { return res && res.ok ? onSuccess() : onRetry(); })
        .catch(onRetry);
    });

    function onSuccess() {
      el.removeAttribute('aria-busy');
      var ok = make('p', 'mos-ofr__success', c.success);
      ok.setAttribute('tabindex', '-1');
      form.replaceWith(ok);
      consent.remove(); // the consent line describes the act just completed
      status.textContent = '';
      ok.focus();
    }
    function onRetry() {
      // Quiet inline retry — never a broken surface.
      el.removeAttribute('aria-busy');
      cta.disabled = false;
      status.textContent = 'Something went wrong — please try again.';
      input.focus();
    }

    /* Mount: append (position:fixed → zero layout shift), record impression,
       then settle in. No focus steal — this is a non-modal aside. */
    document.body.appendChild(el);
    ctl.mount(el); // REQUIRED, exactly once — records the impression
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('mos-ofr--in'); });
    });
  }
})();
